import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';

/**
 * BatteryGridRenderer 컴포넌트
 * 여러 배터리(카트)의 상태를 그리드 형태로 표시하는 컴포넌트
 *
 * === 데이터 형식 1: 배터리/카트 모니터링 (기존) ===
 * {
 *   columns: ["batt_id", "soc", "cart_status", "state", "ext1", ...],
 *   values: {
 *     batt_id: ["CART_01", "CART_02", ...],
 *     soc: [85, 92, ...],
 *     cart_status: [0, 1, 2, 3, ...],  // 0=충전중, 1=대기중, 2=주행중, 3=IOT OFF
 *     state: [0, 1, 2, 3, ...],        // 0=정상, 1=주의, 2=경고, 3=위험
 *     ext1: [...],                     // 선택적 추가 컬럼
 *     extra1: [...]                    // alias (extra{n}) 형태도 함께 전달
 *   }
 * }
 *
 * cart_status 상태 정의 (라비에벨):
 *   0: 충전중 - 카트보관소에서 충전중인 상태
 *   1: 대기중 - 카트보관소에서 통신 연결되어 있는 상태
 *   2: 주행중 - 카트가 외부로 이동해서 통신 연결이 1분간 끊어진 상태
 *   3: IOT OFF - BMS가 OFF 되고 IOT GW의 보조배터리까지 방전된 상태
 *
 * === 데이터 형식 2: 단순 라벨-값 그리드 (신규) ===
 * {
 *   columns: ["cell_label", "value"],  // 또는 임의의 2컬럼
 *   values: {
 *     cell_label: ["셀 1", "셀 2", ...],
 *     value: [4.0, 4.1, ...]
 *   }
 * }
 *
 * 레전드 설정 구조 (options.style.legend):
 * {
 *   // 기존 배터리 모드용
 *   cartStatus: { items: [{ value, label, color }, ...] },
 *   soc: { type: 'range', items: [{ min, max, label, color }, ...] },
 *   state: { items: [{ value, label, border, background }, ...] },
 *   // 단순 라벨-값 모드용
 *   value: { type: 'range', unit: 'V', items: [{ min, max, label, color }, ...] }
 * }
 */

const BatteryGridRenderer = ({ chartData, error, onChartClick, isEditMode }) => {
  const [selectedBattery, setSelectedBattery] = useState(null);
  const containerRef = useRef(null);
  const [cellSize, setCellSize] = useState({ width: 100, height: 120 });
  const resizeDebounceRef = useRef(null);
  
  // 툴팁은 CSS 기반으로 구현 (React 상태 업데이트 없이 - 클릭 이벤트에 영향 없음)

  // 디버깅 로그 제거

  const options = chartData?.options || {};
  const data = chartData?.data || {};
  const baseColumns = ['batt_id', 'soc', 'cart_status', 'state'];

  // 데이터 형식 감지: 단순 라벨-값 모드 vs 배터리 모드
  // 단순 모드: 컬럼이 정확히 2개이고 batt_id가 없는 경우
  const isSimpleLabelValueMode = useMemo(() => {
    if (!Array.isArray(data?.columns)) return false;
    const cols = data.columns.map(c => typeof c === 'string' ? c.toLowerCase() : String(c).toLowerCase());
    // batt_id가 없고 컬럼이 2개인 경우 단순 모드
    return cols.length === 2 && !cols.includes('batt_id');
  }, [data?.columns]);

  // 단순 모드일 때 컬럼명 추출
  const simpleModeColumns = useMemo(() => {
    if (!isSimpleLabelValueMode || !Array.isArray(data?.columns)) return { label: null, value: null };
    return {
      label: data.columns[0],  // 첫 번째 컬럼 = 라벨
      value: data.columns[1]   // 두 번째 컬럼 = 값
    };
  }, [isSimpleLabelValueMode, data?.columns]);
  const dataColumns = useMemo(() => {
    if (Array.isArray(data?.columns) && data.columns.length > 0) {
      return data.columns.map(col => (typeof col === 'string' ? col : String(col)));
    }
    return baseColumns;
  }, [data]);

  const extraColumns = useMemo(() => {
    if (!dataColumns || dataColumns.length <= 4) {
      return [];
    }
    return dataColumns.slice(4);
  }, [dataColumns]);
  
  // 옵션 키를 대소문자 구분 없이 찾는 헬퍼 함수
  const getOption = (optionName, defaultValue) => {
    const keys = Object.keys(options);
    const matchedKey = keys.find(key => key.toLowerCase() === optionName.toLowerCase());
    return matchedKey ? options[matchedKey] : defaultValue;
  };
  
  // visibility 옵션 가져오기 (기본값: 모두 표시)
  // DashboardComponentRenderer에서 전달되는 구조: chartData.options.style.visibility
  const visibility = options?.style?.visibility || {};
  const showBatteryId = visibility.batteryId !== false;
  const showSoc = visibility.soc !== false;
  const showCartStatus = visibility.cartStatus !== false;
  
  // 디버깅 로그 제거
  
  // 배터리 그리드 고유 고급 옵션
  const advanced = options?.advanced || {};
  
  // 그리드 설정: style.advanced 우선 (CommonStylePanel에서 저장하는 위치), 루트 options.advanced는 레거시 폴백
  const gridColumns = (options?.style?.advanced?.gridColumns ?? options?.advanced?.gridColumns ?? getOption('gridColumns', 6));
  // 셀 세로 비율 (가로 길이 대비 %) - 기본값 120% (가로의 1.2배)
  const cellHeightRatio = (options?.style?.advanced?.cellHeightRatio ?? options?.advanced?.cellHeightRatio ?? 120) / 100;
  // showTitle 체크: options.style.visibility.title을 먼저 확인하고, 없으면 레거시 showTitle 확인
  const showTitle = options?.style?.visibility?.title !== false && getOption('showTitle', true) !== false;
  const title = getOption('title', '배터리 상태 모니터링');
  const cellType = (options?.style?.advanced?.cellType ?? options?.advanced?.cellType ?? getOption('cellType', 1));  // 기본값 1 (기존 배터리 아이콘 타입)

  // 제목 스타일(표준 스타일 우선, 레거시 폴백)
  const titleFont = options?.style?.fonts?.title || {};
  const titleFontFamily = titleFont.fontFamily || 'Inter, sans-serif';
  const titleFontSize = titleFont.fontSize ? `${titleFont.fontSize}px` : '20px';
  const titleFontWeight = titleFont.fontWeight || '600';
  const titleColor = titleFont.color || '#2c3e50';

  // 범례 폰트 스타일(표준 스타일 우선)
  const legendFont = options?.style?.fonts?.legend || {};
  const legendFontFamily = legendFont.fontFamily || 'Inter, sans-serif';
  const legendFontSize = legendFont.fontSize ? `${legendFont.fontSize}px` : '11px';
  const legendFontWeight = legendFont.fontWeight || 'normal';
  const legendColor = legendFont.color || '#666';

  // 배터리 ID 폰트 스타일(표준 스타일 우선)
  const batteryIdFont = options?.style?.fonts?.batteryId || {};
  const getBatteryIdStyle = (baseFontSize) => ({
    fontSize: batteryIdFont.fontSize ? `${batteryIdFont.fontSize}px` : `${Math.max(8, Math.min(14, baseFontSize))}px`,
    fontWeight: batteryIdFont.fontWeight || '600',
    fontStyle: batteryIdFont.fontStyle || 'normal',
    color: batteryIdFont.color || '#333',
    fontFamily: batteryIdFont.fontFamily || 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%'
  });

  // SOC 폰트 스타일(표준 스타일 우선)
  const socFont = options?.style?.fonts?.soc || {};
  const getSocStyle = (baseFontSize) => ({
    fontSize: socFont.fontSize ? `${socFont.fontSize}px` : `${Math.max(8, Math.min(12, baseFontSize))}px`,
    fontWeight: socFont.fontWeight || 'bold',
    fontStyle: socFont.fontStyle || 'normal',
    color: socFont.color || '#444',
    fontFamily: socFont.fontFamily || 'inherit',
    whiteSpace: 'nowrap'
  });

  // cart_status 폰트 스타일(표준 스타일 우선)
  const cartStatusFont = options?.style?.fonts?.cartStatus || {};
  const getCartStatusStyle = (baseFontSize) => ({
    fontSize: cartStatusFont.fontSize ? `${cartStatusFont.fontSize}px` : `${Math.max(7, Math.min(11, baseFontSize))}px`,
    fontWeight: cartStatusFont.fontWeight || '600',
    fontStyle: cartStatusFont.fontStyle || 'normal',
    color: cartStatusFont.color || '#333',
    fontFamily: cartStatusFont.fontFamily || 'inherit',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%'
  });

  // 레전드 설정 가져오기 (options.style.legend에서 직접 가져옴, 없으면 null)
  const legendConfig = useMemo(() => {
    return options?.style?.legend || null;
  }, [options?.style?.legend]);

  // 레전드 설정이 있는지 확인
  const hasLegendConfig = legendConfig !== null;

  // 컨테이너 크기에 따른 셀 크기 자동 조정 함수
  const updateCellSize = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;

      // CSS Grid에서 셀 너비 계산: (전체 너비 - gap*(열개수-1) - padding*2) / 열개수
      const gapSize = 8; // grid gap 8px
      const padding = 5; // grid padding 5px
      const totalGapWidth = gapSize * (gridColumns - 1);
      const actualCellWidth = Math.floor((containerWidth - totalGapWidth - padding * 2) / gridColumns);

      // 셀 높이는 너비 * 세로 비율로 계산
      const cellHeight = Math.floor(actualCellWidth * cellHeightRatio);

      // 최소 크기만 제한 (너무 작으면 텍스트가 안 보임), 최대는 제한 없음
      const finalWidth = Math.max(30, actualCellWidth);
      const finalHeight = Math.max(30, cellHeight);

      setCellSize({ width: finalWidth, height: finalHeight });
    }
  }, [gridColumns, cellHeightRatio]);

  // 초기 렌더와 window 리사이즈에 반응
  useLayoutEffect(() => {
    updateCellSize();
    window.addEventListener('resize', updateCellSize);
    return () => window.removeEventListener('resize', updateCellSize);
  }, [updateCellSize]);

  // 컨테이너 자체 리사이즈 감지 (대시보드 카드 리사이즈 등)
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof window === 'undefined') return;

    const hasObserver = typeof window.ResizeObserver !== 'undefined';
    if (!hasObserver) return; // 폴백: window resize 리스너만 사용

    const observer = new ResizeObserver(() => {
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current);
      }
      // 잦은 드래그 리사이즈에 대한 디바운스
      resizeDebounceRef.current = setTimeout(() => {
        updateCellSize();
      }, 80);
    });

    observer.observe(node);

    return () => {
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current);
      }
      observer.disconnect();
    };
  }, [updateCellSize]);

  // SOC에 따른 배터리 필러 색상 (충전량 기반)
  // 레전드 설정이 있으면 사용, 없으면 SOC 값에 따른 그라데이션 색상
  const getBatteryFillColor = useCallback((soc) => {
    // 레전드 설정이 있으면 사용
    if (legendConfig?.soc?.items) {
      for (const item of legendConfig.soc.items) {
        if (soc >= item.min && soc <= item.max) {
          return item.color;
        }
      }
    }
    
    // 레전드 설정이 없으면 SOC 값에 따른 기본 그라데이션
    // 높은 SOC = 녹색, 낮은 SOC = 빨간색
    if (soc >= 80) return '#4CAF50';      // 녹색
    if (soc >= 60) return '#8BC34A';      // 연녹색
    if (soc >= 40) return '#FFC107';      // 노란색
    if (soc >= 20) return '#FF9800';      // 주황색
    return '#F44336';                      // 빨간색
  }, [legendConfig?.soc]);

  // 단순 모드: value 범위에 따른 셀 배경색
  // minExclusive: true면 min < value, false/없으면 min <= value
  // maxExclusive: true면 value < max, false/없으면 value <= max
  const getSimpleValueColor = useCallback((value) => {
    // 레전드 설정이 있으면 사용
    if (legendConfig?.value?.items) {
      for (const item of legendConfig.value.items) {
        const min = item.min ?? -Infinity;
        const max = item.max ?? Infinity;
        const minCheck = item.minExclusive ? value > min : value >= min;
        const maxCheck = item.maxExclusive ? value < max : value <= max;
        if (minCheck && maxCheck) {
          return item.color;
        }
      }
    }

    // 레전드 설정이 없으면 기본 회색
    return '#E8E8E8';
  }, [legendConfig?.value]);

  // 단순 모드: value의 단위 가져오기
  const getSimpleValueUnit = useCallback(() => {
    return legendConfig?.value?.unit || '';
  }, [legendConfig?.value]);
  
  // 값을 숫자로 정규화하는 헬퍼 함수
  // 문자열 state 값도 숫자로 매핑 (백엔드에서 문자열로 올 수 있음)
  const normalizeValue = useCallback((value) => {
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'string') {
      // 먼저 숫자 문자열인지 확인
      const parsed = parseInt(value);
      if (!isNaN(parsed)) {
        return parsed;
      }
      // 문자열 state 매핑 (백엔드: normal/warning/critical/emergency)
      const stringStateMap = {
        'normal': 0,
        'warning': 1,
        'critical': 2,
        'emergency': 3
      };
      const lowerValue = value.toLowerCase();
      if (lowerValue in stringStateMap) {
        return stringStateMap[lowerValue];
      }
    }
    return 0;
  }, []);

  // cart_status에 따른 셀 배경색 (카트 운영 상태 기반)
  // 0=충전중, 1=대기중, 2=주행중, 3=IOT OFF
  // 레전드 설정이 있으면 사용, 없으면 하드코딩된 기본값
  const getCartStatusBackground = useCallback((cartStatus) => {
    const statusNum = normalizeValue(cartStatus);
    
    // 레전드 설정이 있으면 사용
    if (legendConfig?.cartStatus?.items) {
      const item = legendConfig.cartStatus.items.find(i => i.value === statusNum);
      if (item?.color) return item.color;
    }
    
    // 레전드 설정이 없으면 하드코딩된 기본값 사용
    switch (statusNum) {
      case 0: return '#D4E0F0';  // 충전중: 연녹색 (충전 중임을 나타내는 색상)
      case 1: return '#C5FFFC';  // 대기중: 연파란색 (안정적인 대기 상태)
      case 2: return '#FFC5C5';  // 주행중: 연노란색 (이동 중, 주의)
      case 3: return '#E2E2E2';  // IOT OFF: 회색 (비활성 상태)
      default: return '#FFFFFF'; // 기본: 흰색
    }
  }, [legendConfig?.cartStatus, normalizeValue]);

  // cart_status에 따른 상태명 반환
  // 레전드 설정이 있으면 사용, 없으면 하드코딩된 기본값
  const getCartStatusLabel = useCallback((cartStatus) => {
    const statusNum = normalizeValue(cartStatus);
    
    // 레전드 설정이 있으면 사용
    if (legendConfig?.cartStatus?.items) {
      const item = legendConfig.cartStatus.items.find(i => i.value === statusNum);
      if (item?.label) return item.label;
    }
    
    // 레전드 설정이 없으면 하드코딩된 기본값 사용
    switch (statusNum) {
      case 0: return '충전중';
      case 1: return '대기중';
      case 2: return '주행중';
      case 3: return 'IOT OFF';
      default: return '알수없음';
    }
  }, [legendConfig?.cartStatus, normalizeValue]);

  // State에 따른 특별 배경색 (state 2,3만 적용)
  // 레전드 설정이 있으면 사용, 없으면 하드코딩된 기본값
  const getStateOverrideBackground = useCallback((state) => {
    const stateNum = normalizeValue(state);
    
    // 레전드 설정이 있으면 사용
    if (legendConfig?.state?.items) {
      const item = legendConfig.state.items.find(i => i.value === stateNum);
      return item?.background || null;
    }
    
    // 레전드 설정이 없으면 하드코딩된 기본값 사용
    // state 2,3만 특별 배경색 적용 (state 1은 SOH 색상 유지)
    switch (stateNum) {
      case 2: return '#FFE082';    // 노란색 배경 (경고)
      case 3: return '#FFABAB';    // 더 진한 붉은색 배경 (위험)
      default: return null;          // SOH 배경색 사용 (state 0, 1 포함)
    }
  }, [legendConfig?.state, normalizeValue]);

  // state에 따른 상태명 반환
  // 레전드 설정이 있으면 사용, 없으면 하드코딩된 기본값
  const getStateLabel = useCallback((stateValue) => {
    const stateNum = normalizeValue(stateValue);
    
    // 레전드 설정이 있으면 사용
    if (legendConfig?.state?.items) {
      const item = legendConfig.state.items.find(i => i.value === stateNum);
      if (item?.label) return item.label;
    }
    
    // 레전드 설정이 없으면 하드코딩된 기본값 사용
    switch (stateNum) {
      case 0: return '정상';
      case 1: return '주의';
      case 2: return '경고';
      case 3: return '위험';
      default: return '알수없음';
    }
  }, [legendConfig?.state, normalizeValue]);

  // 상태에 따른 테두리 스타일
  // 레전드 설정이 있으면 사용, 없으면 하드코딩된 기본값
  // 주의: 애니메이션은 transform 없이 box-shadow만 사용 (클릭 영역에 영향 없음)
  const getStateBorderStyle = useCallback((state) => {
    const stateNum = normalizeValue(state);

    // 레전드 설정이 있으면 사용
    if (legendConfig?.state?.items) {
      const item = legendConfig.state.items.find(i => i.value === stateNum);

      if (item?.border) {
        // 애니메이션은 state 값에 따라 결정 (0이 아닌 경우 애니메이션 적용)
        const animationSpeed = stateNum === 0 ? 'none' :
                              stateNum === 1 ? 'pulse 2s infinite' :
                              stateNum === 2 ? 'pulse 1.5s infinite' :
                              'pulse 0.7s infinite';
        return {
          border: item.border,
          animation: animationSpeed
        };
      }
    }

    // 레전드 설정이 없으면 하드코딩된 기본값 사용
    switch (stateNum) {
      case 1:  // 주의 (충전 필요)
        return {
          border: '2px solid #3B82F6',  // 푸른색 테두리
          animation: 'pulse 2s infinite'  // 느린 깜빡임
        };
      case 2:  // 경고
        return {
          border: '3px solid #F44336',  // 붉은색 테두리
          animation: 'pulse 1.5s infinite'  // 중간 깜빡임
        };
      case 3:  // 긴급
        return {
          border: '4px solid #D32F2F',  // 더 굵은 붉은색 테두리
          animation: 'pulse 0.7s infinite'  // 더 빠른 깜빡임
        };
      case 0:
      default:
        return {
          border: '1px solid #E0E0E0',  // 기본 테두리 (얇게)
          animation: 'none'
        };
    }
  }, [legendConfig?.state, normalizeValue]);

  // 그리드 아이템 생성을 위한 메모이제이션 (hooks는 조건부 return 전에 호출되어야 함)
  const gridItems = useMemo(() => {
    // 단순 모드인 경우
    if (isSimpleLabelValueMode) {
      const labelCol = simpleModeColumns.label;
      const labels = data.values?.[labelCol] || [];
      if (labels.length === 0) return [];
      return Array.from({ length: labels.length }, (_, i) => i);
    }

    // 배터리 모드: 데이터가 없으면 빈 배열 반환
    if (!data?.values?.batt_id) {
      return [];
    }

    const items = [];
    const count = data.values.batt_id.length;

    // 모든 데이터를 표시 (CSS Grid로 열 개수에 맞게 자동 정렬)
    for (let i = 0; i < count; i++) {
      items.push(i); // 인덱스만 저장, 실제 렌더링은 나중에
    }

    return items;
  }, [data, gridColumns, selectedBattery, cellSize, isSimpleLabelValueMode, simpleModeColumns]);

  // 오류 처리
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#e74c3c',
        fontSize: '14px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <strong>배터리 그리드 오류:</strong> {error.message || String(error)}
      </div>
    );
  }

  // 데이터 검증 (단순 모드와 배터리 모드 분기)
  const hasValidData = isSimpleLabelValueMode
    ? (data.values && simpleModeColumns.label && data.values[simpleModeColumns.label]?.length > 0)
    : (data.values && data.values.batt_id);

  if (!hasValidData) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: '#9e9e9e',
        fontSize: '14px'
      }}>
        데이터를 불러오는 중...
      </div>
    );
  }

  // 배터리 모드용 변수들 (단순 모드에서는 사용하지 않음)
  const { batt_id, soc, cart_status, soh, state } = isSimpleLabelValueMode ? {} : data.values;
  // cart_status 우선, 레거시 soh 폴백 (데이터 소스가 soh 컬럼명을 사용하는 경우)
  const cartStatusData = cart_status ?? soh;
  const batteryCount = isSimpleLabelValueMode ? 0 : (batt_id?.length || 0);

  // 배터리 클릭 처리
  const handleBatteryClick = (index) => {
    console.log('[BatteryGrid] Cell onClick fired', { index, batteryId: batt_id?.[index] });

    if (isEditMode) {
      return;
    }
    
    setSelectedBattery(prevSelected => {
      const newSelected = prevSelected === index ? null : index;
      return newSelected;
    });
    
    if (onChartClick && typeof onChartClick === 'function') {
      const clickData = {
        batteryId: batt_id?.[index],
        soc: soc?.[index],
        cartStatus: cartStatusData?.[index] ?? 0,
        state: state?.[index],
        dataIndex: index,
        chartType: 'battery_grid',
        col1: batt_id?.[index],
        col2: soc?.[index],
        col3: cartStatusData?.[index] ?? 0,
        col4: state?.[index]
      };

      if (extraColumns.length > 0) {
        const extras = {};
        const extraValues = [];
        extraColumns.forEach((columnName, extraIdx) => {
          const alias = `extra${extraIdx + 1}`;
          const columnValues = data.values?.[columnName] ?? data.values?.[alias];
          let value;
          if (Array.isArray(columnValues)) {
            value = columnValues[index];
          } else if (columnValues !== undefined) {
            value = columnValues;
          } else {
            value = null;
          }

          clickData[alias] = value;
          clickData[`col${4 + extraIdx + 1}`] = value;
          clickData[columnName] = value;
          extras[columnName] = value;
          extraValues.push({ alias, columnName, value });
        });

        clickData.extraColumns = extraColumns;
        clickData.extras = extras;
        clickData.extraValues = extraValues;
      }

      onChartClick(clickData, 'self');
    }
  };

  // 단순 라벨-값 셀 클릭 처리
  const handleSimpleCellClick = (index) => {
    if (isEditMode) return;

    setSelectedBattery(prevSelected => prevSelected === index ? null : index);

    if (onChartClick && typeof onChartClick === 'function') {
      const labelCol = simpleModeColumns.label;
      const valueCol = simpleModeColumns.value;
      const labelValue = data.values?.[labelCol]?.[index];
      const valueValue = data.values?.[valueCol]?.[index];

      const clickData = {
        label: labelValue,
        value: valueValue,
        dataIndex: index,
        chartType: 'battery_grid',
        mode: 'simple_label_value',
        col1: labelValue,
        col2: valueValue
      };
      onChartClick(clickData, 'self');
    }
  };

  // 단순 라벨-값 셀 컴포넌트
  const SimpleLabelValueCell = ({ index }) => {
    const labelCol = simpleModeColumns.label;
    const valueCol = simpleModeColumns.value;
    const labels = data.values?.[labelCol] || [];
    const values = data.values?.[valueCol] || [];

    if (index >= labels.length) return null;

    const cellLabel = labels[index];
    const cellValue = values[index];
    const numericValue = typeof cellValue === 'number' ? cellValue : parseFloat(cellValue) || 0;
    const unit = getSimpleValueUnit();
    const bgColor = getSimpleValueColor(numericValue);
    const isSelected = selectedBattery === index;
    const isTopRow = index < gridColumns;

    // 툴팁 텍스트
    const tooltipText = `${cellLabel}\n${numericValue}${unit ? ' ' + unit : ''}`;

    // 라벨 폰트 스타일 (batteryId 스타일 재사용)
    const labelStyle = {
      ...getBatteryIdStyle(cellSize.width / 7),
      textAlign: 'center',
      marginBottom: '4px'
    };

    // 값 폰트 스타일 (soc 스타일 재사용)
    const valueStyle = {
      ...getSocStyle(cellSize.width / 6),
      textAlign: 'center'
    };

    return (
      <div
        className={`battery-cell ${isTopRow ? 'tooltip-bottom' : ''}`}
        onClick={() => handleSimpleCellClick(index)}
        data-tooltip={tooltipText}
        style={{
          width: '100%',
          minWidth: 0,
          height: cellSize.height,
          padding: '8px',
          backgroundColor: bgColor,
          border: isSelected ? '3px solid #2196F3' : '1px solid #E0E0E0',
          borderRadius: '8px',
          cursor: isEditMode ? 'default' : 'pointer',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease, outline 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isSelected ? '0 0 12px 3px rgba(33, 150, 243, 0.6)' : 'none',
          outline: isSelected ? '3px solid rgba(33, 150, 243, 0.3)' : 'none',
          outlineOffset: isSelected ? '2px' : '0px',
          boxSizing: 'border-box',
          overflow: 'visible',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          if (!isEditMode && !isSelected) {
            e.currentTarget.style.outline = '2px solid rgba(100, 100, 100, 0.2)';
            e.currentTarget.style.outlineOffset = '1px';
          }
        }}
        onMouseLeave={(e) => {
          if (!isEditMode && !isSelected) {
            e.currentTarget.style.outline = 'none';
            e.currentTarget.style.outlineOffset = '0px';
          }
        }}
      >
        <div style={{ pointerEvents: 'none', width: '100%', textAlign: 'center' }}>
          {/* 라벨 */}
          <div style={labelStyle}>{cellLabel}</div>
          {/* 값 + 단위 */}
          <div style={valueStyle}>
            {numericValue}{unit ? ` ${unit}` : ''}
          </div>
        </div>
      </div>
    );
  };

  // 배터리 셀 컴포넌트
  const BatteryCell = ({ index }) => {
    if (index >= batteryCount) return null;

    const battId = batt_id[index];
    const battSoc = soc[index] || 0;
    const battCartStatus = cartStatusData?.[index] ?? 0;  // 기본값 0 (충전중)
    const battState = state[index] ?? 0;  // 기본값 0 (정상)
    const isSelected = selectedBattery === index;

    // SOC를 0-100 범위로 제한
    const socPercent = Math.max(0, Math.min(100, battSoc));
    const fillColor = getBatteryFillColor(battSoc);  // SOC 기반 색상
    const stateOverrideBg = getStateOverrideBackground(battState);  // state 2,3의 특별 배경
    const bgColor = stateOverrideBg || getCartStatusBackground(battCartStatus);  // cart_status 기반 배경색 (state 우선)
    const borderStyle = getStateBorderStyle(battState);  // 상태 기반 테두리
    const cartStatusLabel = getCartStatusLabel(battCartStatus);  // 카트 상태 라벨
    
    // 디버깅 로그 제거
    
    // 알람 상태에 따른 기본 box-shadow (state가 0이 아닐 때)
    const alarmShadow = battState !== 0 && battState !== 'normal' ? '0 0 8px rgba(0,0,0,0.2)' : 'none';

    // 툴팁 텍스트 생성
    const tooltipText = `🔋 ${battId}\nSOC: ${battSoc}%\n상태: ${cartStatusLabel}\n알람: ${getStateLabel(battState)}`;

    // 상단 행인지 확인 (첫 번째 행은 툴팁을 아래에 표시)
    const isTopRow = index < gridColumns;

    return (
      <div
        className={`battery-cell ${isTopRow ? 'tooltip-bottom' : ''}`}
        onClick={() => handleBatteryClick(index)}
        data-tooltip={tooltipText}
        style={{
          // CSS Grid를 사용하므로 width는 자동, height만 비율로 계산
          width: '100%',
          minWidth: 0,  // Grid 셀이 콘텐츠에 의해 강제 확장되지 않도록
          height: cellSize.height,
          padding: '8px',
          backgroundColor: bgColor,
          ...borderStyle,
          border: isSelected ? '3px solid #2196F3' : borderStyle.border,
          borderRadius: '8px',
          cursor: isEditMode ? 'default' : 'pointer',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease, outline 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          // 선택 시 box-shadow로 강조, 알람 상태일 때도 그림자 표시
          boxShadow: isSelected
            ? '0 0 12px 3px rgba(33, 150, 243, 0.6)'
            : alarmShadow,
          // 선택 시 outline으로 확대 효과 대체 (클릭 영역에 영향 없음)
          outline: isSelected ? '3px solid rgba(33, 150, 243, 0.3)' : 'none',
          outlineOffset: isSelected ? '2px' : '0px',
          boxSizing: 'border-box',
          overflow: 'visible',  // 툴팁이 보이도록 visible
          position: 'relative'  // 툴팁 위치 기준
        }}
        onMouseEnter={(e) => {
          // 호버 효과: outline으로 확대된 것처럼 보이게 (클릭 영역 변경 없음)
          if (!isEditMode && !isSelected) {
            e.currentTarget.style.outline = '2px solid rgba(100, 100, 100, 0.2)';
            e.currentTarget.style.outlineOffset = '1px';
          }
        }}
        onMouseLeave={(e) => {
          // 호버 효과 제거
          if (!isEditMode && !isSelected) {
            e.currentTarget.style.outline = 'none';
            e.currentTarget.style.outlineOffset = '0px';
          }
        }}
      >
        {/* 셀 내부 콘텐츠 래퍼 - 클릭 이벤트가 부모 셀로 전달되도록 pointerEvents: none */}
        <div style={{
          pointerEvents: 'none',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        {/* cellType에 따른 렌더링 */}
        {cellType === 1 ? (
          <>
            {/* Type 1: 기존 배터리 아이콘 스타일 */}
            {/* 배터리 ID */}
            {showBatteryId && (
              <div style={{
                ...getBatteryIdStyle(cellSize.width / 8),
                textAlign: 'center',
                marginBottom: '4px'
              }}>
                {battId}
              </div>
            )}
            
            {/* 배터리 게이지 */}
        <div style={{
          position: 'relative',
          width: '60%',
          height: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 60 100"
            style={{ maxWidth: '60px', maxHeight: '100px' }}
          >
            {/* 배터리 외곽선 */}
            <rect
              x="5"
              y="10"
              width="50"
              height="85"
              rx="4"
              fill="none"
              stroke="#666"
              strokeWidth="2"
            />
            {/* 배터리 상단 단자 */}
            <rect
              x="22"
              y="5"
              width="16"
              height="8"
              rx="2"
              fill="#666"
            />
            {/* 배터리 충전량 표시 (SOC 기반 높이 및 색상) */}
            <rect
              key={`fill-${index}-${battCartStatus}`}
              x="8"
              y={13 + (85 - 6) * (1 - socPercent / 100)}
              width="44"
              height={(85 - 6) * (socPercent / 100)}
              rx="2"
              fill={fillColor}
              opacity="1"
            />
            {/* 충전량 구분선 */}
            {[20, 40, 60, 80].map(level => (
              <line
                key={level}
                x1="5"
                y1={13 + (85 - 6) * (1 - level / 100)}
                x2="55"
                y2={13 + (85 - 6) * (1 - level / 100)}
                stroke="#999"
                strokeWidth="0.5"
                opacity="0.3"
              />
            ))}
          </svg>
          
          {/* SOC 텍스트 표시 */}
          {showSoc && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              ...getSocStyle(cellSize.width / 6),
              color: socFont.color || (socPercent > 50 ? '#fff' : '#333'),
              textShadow: socPercent > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
            }}>
              {battSoc}%
            </div>
          )}
        </div>
        
            {/* 카트 상태 표시 */}
            {showCartStatus && (
              <div style={{
                ...getCartStatusStyle(cellSize.width / 9),
                marginTop: '4px'
              }}>
                {cartStatusLabel}
              </div>
            )}
          </>
        ) : cellType === 2 ? (
          <>
            {/* Type 2: 가로 게이지 스타일 */}
            {/* 배터리 ID */}
            {showBatteryId && (
              <div style={{
                ...getBatteryIdStyle(cellSize.width / 7),
                textAlign: 'center',
                marginBottom: '8px'
              }}>
                {battId}
              </div>
            )}
            
            {/* 가로 게이지 바 */}
            <div style={{
              position: 'relative',
              width: '80%',
              height: '15px',  // 30px에서 15px로 절반 감소
              backgroundColor: '#f0f0f0',
              borderRadius: '7px',  // border-radius도 비례하여 조정
              border: '1px solid #ddd',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              {/* 게이지 채움 */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${socPercent}%`,
                backgroundColor: fillColor,
                borderRadius: '7px',  // border-radius도 비례하여 조정
                transition: 'width 0.3s ease'
              }} />
            </div>
            
            {/* SOC 표시 (게이지 아래) */}
            {showSoc && (
              <div style={{
                ...getSocStyle(cellSize.width / 8)
              }}>
                SOC {battSoc}%
              </div>
            )}
          </>
        ) : cellType === 3 ? (
          <>
            {/* Type 3: 가로 게이지 스타일 (게이지 내 SOC, 아래 SOH) */}
            {/* 배터리 ID */}
            {showBatteryId && (
              <div style={{
                ...getBatteryIdStyle(cellSize.width / 7),
                textAlign: 'center',
                marginBottom: '8px'
              }}>
                {battId}
              </div>
            )}
            
            {/* 가로 게이지 바 */}
            <div style={{
              position: 'relative',
              width: '80%',
              height: '15px',
              backgroundColor: '#f0f0f0',
              borderRadius: '7px',
              border: '1px solid #ddd',
              overflow: 'hidden',
              marginBottom: '8px'
            }}>
              {/* 게이지 채움 */}
              <div style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${socPercent}%`,
                backgroundColor: fillColor,
                borderRadius: '7px',
                transition: 'width 0.3s ease'
              }} />
              
              {/* SOC 텍스트 (게이지 내부) */}
              {showSoc && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  ...getSocStyle(10),
                  fontSize: socFont.fontSize ? `${socFont.fontSize}px` : '10px',
                  color: socFont.color || (socPercent > 50 ? '#fff' : '#333'),
                  textShadow: socPercent > 50 ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
                  zIndex: 1
                }}>
                  {battSoc}%
                </div>
              )}
            </div>
            
            {/* 카트 상태 표시 (게이지 아래) */}
            {showCartStatus && (
              <div style={{
                ...getCartStatusStyle(cellSize.width / 9)
              }}>
                {cartStatusLabel}
              </div>
            )}
          </>
        ) : null}
        </div>
      </div>
    );
  };


  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '10px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        // transparent 옵션 활성화 시 배경 투명 처리 (컨테이너 레벨)
        backgroundColor: getOption('transparent', false) === true ? 'transparent' : undefined,
        position: 'relative'  // 툴팁 위치 기준점
      }}
    >
      {/* 애니메이션 및 툴팁 스타일 */}
      <style>
        {`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0.3; }
          }
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 87, 34, 0.7);
              outline: 2px solid transparent;
            }
            50% {
              box-shadow: 0 0 15px 6px rgba(255, 87, 34, 0.4);
              outline: 2px solid rgba(255, 87, 34, 0.3);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 87, 34, 0);
              outline: 2px solid transparent;
            }
          }

          /* CSS 기반 툴팁 - React 상태 업데이트 없이 작동하여 클릭에 영향 없음 */
          .battery-cell::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%) translateY(-8px);
            background-color: rgba(45, 55, 72, 0.95);
            color: #fff;
            padding: 10px 14px;
            border-radius: 8px;
            font-size: 12px;
            line-height: 1.6;
            white-space: pre-line;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
            z-index: 1000;
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.2s ease, visibility 0.2s ease;
            min-width: 120px;
            text-align: left;
          }

          /* 툴팁 화살표 */
          .battery-cell::before {
            content: '';
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%) translateY(4px);
            border: 6px solid transparent;
            border-top-color: rgba(45, 55, 72, 0.95);
            z-index: 1001;
            pointer-events: none;
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.2s ease, visibility 0.2s ease;
          }

          /* 호버 시 툴팁 표시 */
          .battery-cell:hover::after,
          .battery-cell:hover::before {
            opacity: 1;
            visibility: visible;
          }

          /* 상단 행: 툴팁을 아래쪽에 표시 */
          .battery-cell.tooltip-bottom::after {
            bottom: auto;
            top: 100%;
            transform: translateX(-50%) translateY(8px);
          }

          .battery-cell.tooltip-bottom::before {
            bottom: auto;
            top: 100%;
            transform: translateX(-50%) translateY(-4px);
            border-top-color: transparent;
            border-bottom-color: rgba(45, 55, 72, 0.95);
          }
        `}
      </style>
      
      {/* 타이틀 */}
      {showTitle && (
        <div style={{
          fontSize: titleFontSize,
          fontWeight: titleFontWeight,
          color: titleColor,
          fontFamily: titleFontFamily,
          textAlign: 'center',
          marginBottom: '15px',
          height: '30px'
        }}>
          {title}
        </div>
      )}
      
      {/* 배터리 그리드 */}
      <div
        onClick={(e) => {
          // 디버깅: 그리드 영역에서 클릭된 요소 확인
          console.log('[BatteryGrid Grid Area] Click', {
            target: e.target,
            tagName: e.target?.tagName,
            textContent: e.target?.textContent?.substring(0, 30),
            className: e.target?.className
          });
        }}
        style={{
          flex: 1,
          width: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
          gap: '8px',
          padding: '5px',
          alignContent: 'flex-start',
          overflowY: 'auto',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          position: 'relative',
          isolation: 'isolate'  // 새로운 스태킹 컨텍스트 생성하여 애니메이션 영향 격리
        }}>
        {gridItems.map(index => (
          isSimpleLabelValueMode
            ? <SimpleLabelValueCell key={`simple-${index}-${gridColumns}`} index={index} />
            : <BatteryCell key={`${index}-${gridColumns}`} index={index} />
        ))}
      </div>
      
      {/* 툴팁 비활성화 - 클릭 이벤트 문제 디버깅용 */}
      
      {/* 범례 (showLegend 옵션에 따라 표시) */}
      {(options?.style?.visibility?.legend !== false) && (
      <div style={{
        marginTop: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        fontSize: legendFontSize,
        color: legendColor,
        fontFamily: legendFontFamily,
        fontWeight: legendFontWeight
      }}>
        {hasLegendConfig ? (
          <>
            {/* 동적 범례 - 레전드 설정이 있을 때 */}

            {/* 단순 모드: value 범례 - title과 items가 있을 때만 표시 */}
            {/* 같은 label은 중복 제거하여 한 번만 표시 */}
            {legendConfig?.value?.title && legendConfig?.value?.items && legendConfig.value.items.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '600' }}>{legendConfig.value.title}:</span>
              {legendConfig.value.items
                .filter((item, index, arr) => arr.findIndex(i => i.label === item.label) === index)
                .map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{
                    width: '20px',
                    height: '12px',
                    backgroundColor: item.color,
                    borderRadius: '2px',
                    border: '1px solid #ddd'
                  }} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            )}

            {/* 테두리/배경 범례 - title과 items가 있을 때만 표시 */}
            {legendConfig?.state?.title && legendConfig?.state?.items && legendConfig.state.items.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '600' }}>{legendConfig.state.title}:</span>
              {legendConfig.state.items.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '12px', 
                    backgroundColor: item.background || '#FFFFFF', 
                    border: item.border || '1px solid #E0E0E0', 
                    borderRadius: '2px' 
                  }} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            )}
            
            {/* 게이지 색상 범례 - title과 items가 있을 때만 표시 */}
            {legendConfig?.soc?.title && legendConfig?.soc?.items && legendConfig.soc.items.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '600' }}>{legendConfig.soc.title}:</span>
              {legendConfig.soc.items.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '12px', 
                    backgroundColor: item.color, 
                    borderRadius: '2px' 
                  }} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            )}
            
            {/* 셀 배경색 범례 - title과 items가 있을 때만 표시 */}
            {legendConfig?.cartStatus?.title && legendConfig?.cartStatus?.items && legendConfig.cartStatus.items.length > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '600' }}>{legendConfig.cartStatus.title}:</span>
              {legendConfig.cartStatus.items.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <div style={{ 
                    width: '20px', 
                    height: '12px', 
                    backgroundColor: item.color, 
                    borderRadius: '2px', 
                    border: '1px solid #ddd' 
                  }} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            )}
          </>
        ) : (
          <>
            {/* 기본 범례 - 레전드 설정이 없을 때 하드코딩 */}
            {/* 상태 범례 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '600' }}>상태:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#FFFFFF', border: '1px solid #E0E0E0', borderRadius: '2px' }} />
                <span>정상</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#FFE082', border: '3px solid #F44336', borderRadius: '2px' }} />
                <span>주의(붉은테두리+노란배경)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#FFABAB', border: '4px solid #D32F2F', borderRadius: '2px' }} />
                <span>긴급(붉은테두리+붉은배경)</span>
              </div>
            </div>
            
            {/* SOC 범례 (충전량) */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '600' }}>SOC:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#4CAF50', borderRadius: '2px' }} />
                <span>≥80%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#8BC34A', borderRadius: '2px' }} />
                <span>60-79%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#FFC107', borderRadius: '2px' }} />
                <span>40-59%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#FF9800', borderRadius: '2px' }} />
                <span>20-39%</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#F44336', borderRadius: '2px' }} />
                <span>{'<20%'}</span>
              </div>
            </div>
            
            {/* 카트 상태 범례 (배경색) */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '15px'
            }}>
              <span style={{ fontWeight: '600' }}>카트상태(배경):</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#D4E0F0', borderRadius: '2px', border: '1px solid #ddd' }} />
                <span>충전중</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#C5FFFC', borderRadius: '2px', border: '1px solid #ddd' }} />
                <span>대기중</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#FFC5C5', borderRadius: '2px', border: '1px solid #ddd' }} />
                <span>주행중</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <div style={{ width: '20px', height: '12px', backgroundColor: '#E2E2E2', borderRadius: '2px', border: '1px solid #ddd' }} />
                <span>IOT OFF</span>
              </div>
            </div>
          </>
        )}
      </div>
      )}
    </div>
  );
};

export default BatteryGridRenderer;
